import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useCallback } from 'react';

export interface NotificationPayload {
  recipient?: string | string[];
  title: string;
  description?: string;
  link?: string;
  severity?: 'critical' | 'high' | 'normal' | 'low';
  topic?: string;
}

export interface UseNotificationResult {
  sendNotification: (payload: NotificationPayload) => Promise<void>;
  getCurrentUser: () => Promise<string | undefined>;
}

/**
 * Hook to send notifications using the DevHub notifications backend
 * @returns Object with sendNotification function and getCurrentUser function
 */
export const useNotification = (): UseNotificationResult => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const getCurrentUser = useCallback(async (): Promise<string | undefined> => {
    try {
      const identity = await identityApi.getBackstageIdentity();
      return identity.userEntityRef;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to get current user identity:', error);
      return undefined;
    }
  }, [identityApi]);

  const sendNotification = useCallback(
    async (payload: NotificationPayload): Promise<void> => {
      try {
        const { token } = await identityApi.getCredentials();

        const response = await fetch(
          `${backendUrl}/api/devhub-notifications/notify`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to send notification');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to send notification:', error);
        throw error;
      }
    },
    [backendUrl, identityApi],
  );

  return {
    sendNotification,
    getCurrentUser,
  };
};
